import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActiveSubscriptionPlans,
  getPaymentMethods,
  addPaymentMethod,
  getInvoices,
  getInvoice,
  updateSubscriptionPaymentMethod,
  getSubscriptions,
} from 'src/services/billing';
import { downloadBillingHistoryAsCSV } from 'src/utils/csvUtils';

export const useBilling = () => {
  const queryClient = useQueryClient();

  const { data: subscriptions, isLoading: isLoadingSubscriptions, refetch: refetchSubscriptions } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: getSubscriptions,
  });

  const { data: subscriptionPlans, isLoading: isLoadingPlans, refetch: refetchSubscriptionPlans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: getActiveSubscriptionPlans,
  });

  const { data: paymentMethods, isLoading: isLoadingPaymentMethods, refetch: refetchPaymentMethods } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: getPaymentMethods,
  });

  const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: getInvoices,
  });

  const mutationAddPaymentMethod = useMutation({
    mutationFn: addPaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });

  const mutationUpdateSubscriptionPaymentMethod = useMutation({
    mutationFn: ({ subscriptionId, paymentMethodId }: { subscriptionId: number; paymentMethodId: string }) =>
      updateSubscriptionPaymentMethod(subscriptionId, paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });

  const refetch = React.useCallback(async () => {
    await Promise.all([
      refetchSubscriptions(),
      refetchSubscriptionPlans(),
      refetchPaymentMethods(),
    ]);
  }, [refetchSubscriptions, refetchSubscriptionPlans, refetchPaymentMethods]);

  return {
    subscriptionPlans,
    paymentMethods,
    invoices,
    isLoading: isLoadingPlans || isLoadingPaymentMethods || isLoadingInvoices || isLoadingSubscriptions,
    addPaymentMethod: mutationAddPaymentMethod.mutate,
    updateSubscriptionPaymentMethod: mutationUpdateSubscriptionPaymentMethod.mutateAsync,
    downloadBillingHistoryAsCSV,
    subscriptions,
    refetch,
  };
};

export const useInvoice = (invoiceId: string) => {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: !!invoiceId,
  });
};
